import { useEffect, useRef } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { useLocation, useNavigate } from 'Pods/Shared/ContextBasedRouter'
import { setGenerationResult } from 'Pods/Shared/GenerationStatus/actions'
import { buildLastResult } from 'Pods/Shared/GenerationStatus/buildLastResult'
import useApiGet from 'Pods/Shared/Hooks/useApiGet'
import { VARIATION_TYPES, RUN_TERMINAL_STATUSES } from 'Pods/ThreeDStudio/constants'
import { buildSharedRunPathname } from 'Pods/ThreeDStudio/Hooks/buildSharedRunPathname'
import { parseGeneratingRunId } from 'Pods/ThreeDStudio/Hooks/generatingRunUrl'
import { parseRunIdFromPathname } from 'Pods/ThreeDStudio/Hooks/parseRunIdFromPathname'
import { useThreeDStudioStore, threeDStudioStore } from 'Pods/ThreeDStudio/Store'
import {
  setHasGenerated,
  setHasFailed,
  setAvailableCredits,
  setErrorDetails,
  removeActiveRun,
  updateActiveRunByRunId,
} from 'Pods/ThreeDStudio/Store/actions/generation'
import { applySubscriptionQuota } from 'Pods/ThreeDStudio/Store/actions/subscription'
import { setOpenedModel, openVariationsTab } from 'Pods/ThreeDStudio/Store/actions/ui'

// A run that reached one of these before this subscription's server-side stream_from
// registered will never receive its terminal broadcast (ActionCable does not replay),
// so connected() reconciles against /runs/:id to catch an already-settled run. Mirrors
// Generator::Run#broadcast_terminal_update? on the backend.
const TERMINAL_STATUSES = RUN_TERMINAL_STATUSES

// Maintains one GeneratorRunsChannel subscription per in-flight run (paid tiers can
// run several generations concurrently), settling each run independently as its
// terminal broadcast arrives.
export default function useGeneratorRunChannel() {
  // Key the effect on the set of subscribed run ids, not the runs array identity, so
  // unrelated run patches (e.g. settings arriving after the POST) do not resubscribe.
  const activeRunIdsKey = useThreeDStudioStore(state => (
    state.generation.activeRuns
      .filter(run => run.runId != null)
      .map(run => String(run.runId))
      .join(',')
  ))
  const subscriptionsRef = useRef(new Map())
  // A connect-time reconcile and a live broadcast can both resolve for the same run;
  // only the first drives the store to its terminal state (see handleTerminalUpdate).
  const settledRunIdsRef = useRef(new Set())
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const { getData } = useApiGet()
  // Capture latest navigate/location via refs so the channel subscriptions
  // do not re-create themselves when these identities change between renders.
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate
  const locationRef = useRef(location)
  locationRef.current = location
  // getData's identity churns when Sentry finishes loading (its useSentry dep flips), so
  // keep it in a ref too - otherwise the effect tears down and re-subscribes mid-generation
  // and could miss the completion broadcast. Mirrors getDataRef in useGenerationStatusWatcher.
  const getDataRef = useRef(getData)
  getDataRef.current = getData

  useEffect(() => {
    if (!window.App?.cable?.subscriptions) {
      return
    }

    function unsubscribeRun(runIdKey) {
      const subscription = subscriptionsRef.current.get(runIdKey)
      if (subscription) {
        subscription.unsubscribe()
        subscriptionsRef.current.delete(runIdKey)
      }
    }

    // Drives the store to one run's terminal state. Invoked from that run's live
    // `received` broadcast and from its connect-time reconcile, whichever lands first.
    function handleTerminalUpdate(runIdKey, attributes) {
      if (settledRunIdsRef.current.has(runIdKey)) {
        return
      }
      settledRunIdsRef.current.add(runIdKey)

      unsubscribeRun(runIdKey)
      removeActiveRun({ runId: runIdKey })

      const {
        outputAssets,
        createdAt,
        remainingCredits,
        threeDStudioQuota,
        failureReason,
        failureMessage,
        providerErrorCode,
        providerModelId,
        generatorSessionId,
      } = attributes
      setAvailableCredits(remainingCredits?.total_credits)
      applySubscriptionQuota(threeDStudioQuota)

      const fileUrl = outputAssets?.[0]?.fileUrl
      if (!fileUrl) {
        setErrorDetails({ failureReason, failureMessage, providerErrorCode })
        setHasFailed(true)
        setGenerationResult(buildLastResult(attributes))
        return
      }

      setErrorDetails({})

      const openedModel = { ...attributes, createdAt: createdAt || new Date().toISOString() }

      // URL-is-source-of-truth: completion may hydrate the store only
      // when the URL already points at this run, or after replacing /ai
      // with this run's URL. Gallery and other model URLs are left alone;
      // the library refetch below will surface the completed run there.
      const currentPathname = locationRef.current?.pathname
      const completedRunId = attributes?.id
      const completedRunIdKey = completedRunId == null ? null : String(completedRunId)
      const urlRunId = parseRunIdFromPathname(currentPathname)
      const urlRunIdKey = urlRunId == null ? null : String(urlRunId)
      // /ai carries the in-flight run the user is watching in `?run=` (see
      // buildGeneratingRunUrl), so the pathname alone no longer means "nothing is open".
      // With several generations running, whichever finishes first must not drag the user
      // off the card they are watching - only claim the view when no run is selected or
      // the selected one is the run that just finished.
      const watchedRunId = parseGeneratingRunId(locationRef.current?.search ?? '')
      const isWatchingAnotherRun = watchedRunId != null && String(watchedRunId) !== completedRunIdKey
      const completedRunPathname = buildSharedRunPathname({
        slug: attributes.slug,
        id: completedRunId,
        title: attributes.title ?? attributes.prompt,
      })

      const isEnhancementRun = !!VARIATION_TYPES[providerModelId]?.isEnhancement
      const isSegmentationRun = !!VARIATION_TYPES[providerModelId]?.isSegmentation
      const openedSessionId = threeDStudioStore.getState().ui?.openedModel?.generatorSessionId
      const isEnhancementOfOpenSession = isEnhancementRun
        && generatorSessionId != null
        && String(generatorSessionId) === String(openedSessionId)

      if (isEnhancementOfOpenSession && isSegmentationRun) {
        // Segmentation results stay in the Segment tab instead of jumping to
        // Variations. Hydrate the viewer from the broadcast payload (the
        // URL-sync refetch can miss the freshly-created private run) and align
        // the URL without forcing a tab switch.
        setOpenedModel(openedModel)
        navigateRef.current(completedRunPathname, { replace: true })
      } else if (isEnhancementOfOpenSession) {
        // A remesh/retexture adds a new variation to the open session rather
        // than a new library item. Open the Variations panel and select the
        // new variation: hydrate the viewer straight from the broadcast payload
        // (the URL-sync refetch can miss the freshly-created private run) so the
        // viewport shows the new variation, then align the URL.
        openVariationsTab()
        setOpenedModel(openedModel, { keepVariationsTab: true })
        navigateRef.current(completedRunPathname, { replace: true, state: { keepVariationsTab: true } })
      } else if (completedRunIdKey && currentPathname === '/ai' && !isWatchingAnotherRun) {
        navigateRef.current(completedRunPathname, { replace: true })
        setOpenedModel(openedModel)
      } else if (completedRunIdKey && urlRunIdKey === completedRunIdKey) {
        setOpenedModel(openedModel)
      }

      queryClient.invalidateQueries({ queryKey: ['variations'] })
      queryClient.refetchQueries({ queryKey: ['libraryItems', 'sessions'], exact: false })
      setHasGenerated(true)
      // Prebuilt pathname so consumers (top nav, etc.) can link to the model
      // without depending on the ThreeDStudio pod's router helper.
      setGenerationResult(buildLastResult(attributes, { pathname: completedRunPathname }))
    }

    // Catch a run that already settled before this subscription's stream_from registered:
    // its terminal broadcast fired into the void (ActionCable does not replay), so without
    // this the spinner hangs forever. Fast failures (~a couple of seconds) hit this far
    // more than successes, which take long enough to generate that the subscription is
    // always ready first.
    function reconcileOnConnect(runIdKey) {
      getDataRef.current({
        path: `/api/internal/generator/runs/${runIdKey}`,
        onSuccess: (response) => {
          const attributes = response?.data?.data?.attributes
          if (!attributes) {
            return
          }

          if (TERMINAL_STATUSES.has(attributes.status)) {
            handleTerminalUpdate(runIdKey, attributes)
            return
          }

          // Phase broadcasts are deduplicated server-side and never replayed, so a run
          // already past 'queued' when this subscription registered would stay stuck on
          // the seeded phase without this (the serializer derives the current one).
          if (attributes.phase) {
            updateActiveRunByRunId(runIdKey, {
              phase: attributes.phase,
              phaseUpdatedAt: attributes.phaseUpdatedAt,
            })
          }
        },
      })
    }

    function subscribeRun(runIdKey) {
      subscriptionsRef.current.set(runIdKey, window.App.cable.subscriptions.create(
        { channel: 'GeneratorRunsChannel', run_id: runIdKey },
        {
          connected() {
            reconcileOnConnect(runIdKey)
          },
          rejected() {
            if (typeof window !== 'undefined' && window.showNotification) {
              window.showNotification('Connection failed. Please try again.', { type: 'error', autohide: false })
            }
            unsubscribeRun(runIdKey)
            removeActiveRun({ runId: runIdKey })
          },
          received(message) {
            if (message?.type === 'generator/runs/phase') {
              const { phase, phaseUpdatedAt } = message?.data || {}
              updateActiveRunByRunId(runIdKey, { phase, phaseUpdatedAt })
              return
            }

            if (message?.type === 'generator/runs/queue_position') {
              const { queuePosition, queueSize } = message?.data || {}
              updateActiveRunByRunId(runIdKey, { queuePosition, queueSize })
              return
            }

            if (message?.type !== 'generator/runs/update') {
              return
            }

            handleTerminalUpdate(runIdKey, message?.data?.attributes || {})
          },
        }
      ))
    }

    const desiredRunIds = new Set(activeRunIdsKey.split(',').filter(Boolean))

    Array.from(subscriptionsRef.current.keys()).forEach((runIdKey) => {
      if (!desiredRunIds.has(runIdKey)) {
        unsubscribeRun(runIdKey)
      }
    })

    desiredRunIds.forEach((runIdKey) => {
      if (!subscriptionsRef.current.has(runIdKey) && !settledRunIdsRef.current.has(runIdKey)) {
        subscribeRun(runIdKey)
      }
    })
  }, [activeRunIdsKey, queryClient])

  // Full teardown only on unmount; the diffing effect above keeps live runs
  // subscribed across re-renders without a resubscribe gap.
  useEffect(() => () => {
    subscriptionsRef.current.forEach(subscription => subscription.unsubscribe())
    subscriptionsRef.current.clear()
  }, [])
}
